import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { fetchOnboarding, recordConsents, saveOnboarding } from '../lib/onboarding';
import {
  CONSENTS,
  ONBOARDING_STEPS,
  TOTAL_STEPS,
  type ConsentKind,
} from '../constants/onboarding';
import { aiSourcesAllowed, canAdvance, type OnboardingAnswers } from '../utils/onboardingFlow';
import { useUserStore } from './userStore';
import { useBodyAtlasStore } from './bodyAtlasStore';
import { useCurrentView } from '../hooks/useCurrentView';
import { LANDING_VIEW } from '../lib/viewRouter';
import { useLocationStore } from './locationStore';
import { formatDateToLocal } from '../utils/date';
import { accountGeneration, accountStorage, scopeToAccount } from '../lib/accountStorage';

/**
 * The onboarding sequence's state: where the person is, what they have
 * answered, and whether they have finished.
 *
 * ⚠️ `hasCompletedOnboarding` keeps riding `user_preferences.onboarding_completed`
 * exactly as before — cloud `true` wins, so once onboarded anywhere you stay
 * onboarded. The answers are new and live in `user_onboarding` (migration 0255).
 *
 * ⚠️⚠️ THE SAVED COPY IS PER ACCOUNT (lib/accountStorage). It was one key for the
 * device, and `initialize` ORs the cloud flag with the saved one — so a NEW account
 * on a device where someone had finished skipped onboarding entirely, and the
 * answers (name, date of birth, city, gender) were the last person's. Worse: a
 * sign-out mid-sequence left the overlay OPEN, and the next account pressing
 * through it wrote those answers into its own profile.
 */

const defaultAnswers = (): OnboardingAnswers => ({
  reasons: [],
  preferredName: '',
  fullName: '',
  dateOfBirth: '',
  city: null,
  gender: null,
  consents: Object.fromEntries(
    CONSENTS.map((c) => [c.kind, c.required ? false : c.defaultGranted]),
  ) as Record<ConsentKind, boolean>,
});

interface OnboardingState {
  hasCompletedOnboarding: boolean;
  /** True while `complete()` is in flight. NOT persisted — see `partialize`. */
  isCompleting: boolean;
  isOnboardingVisible: boolean;
  /** 1-based, matching `user_onboarding.current_step`. */
  stepIndex: number;
  answers: OnboardingAnswers;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  setHasCompletedOnboarding: (completed: boolean) => void;
  setOnboardingVisible: (visible: boolean) => void;
  startOnboarding: () => void;
  setAnswers: (patch: Partial<OnboardingAnswers>) => void;
  toggleReason: (key: string) => void;
  setConsent: (kind: ConsentKind, granted: boolean) => void;
  next: () => void;
  back: () => void;
  complete: () => Promise<void>;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist<OnboardingState, [], [], Partial<OnboardingState>>(
    (set, get) => ({
      hasCompletedOnboarding: false,
      isCompleting: false,
      isOnboardingVisible: false,
      stepIndex: 1,
      answers: defaultAnswers(),
      initializedUserId: null,

      // The "has completed onboarding" flag rides the shared user_preferences
      // row. Cloud true wins (once onboarded anywhere, stay onboarded).
      initialize: async () => {
        // ⚠️ Answers for an account that has since signed out are dropped (lib/accountStorage).
        const at = accountGeneration();
        const res = await fetchUserPreferences();
        if (!res || at !== accountGeneration()) return;
        if (get().initializedUserId === res.userId) return;
        set((state) => ({
          hasCompletedOnboarding:
            res.row?.onboarding_completed === true ? true : state.hasCompletedOnboarding,
          initializedUserId: res.userId,
        }));

        /* Resume. ⚠️ Only for someone who has NOT finished — re-reading answers
           for a completed user would let a stale local cache overwrite a name
           they have since changed in Settings. */
        if (get().hasCompletedOnboarding) return;
        const saved = await fetchOnboarding();
        const row = saved?.row;
        if (!row || at !== accountGeneration()) return;
        set((state) => ({
          // ⚠️ Clamp. The stored step is bounded 1..13 by the database today,
          // but a build that removed a step would read a number past the end
          // and render nothing at all.
          stepIndex: Math.min(Math.max(row.current_step ?? 1, 1), TOTAL_STEPS),
          answers: {
            ...state.answers,
            reasons: row.reasons ?? state.answers.reasons,
          },
        }));
      },

      setHasCompletedOnboarding: (completed) => {
        set({ hasCompletedOnboarding: completed });
        void saveUserPreference({ onboarding_completed: completed });
      },
      setOnboardingVisible: (visible) => set({ isOnboardingVisible: visible }),

      /** Settings → "Replay the introduction". Starts from the top. */
      startOnboarding: () => set({ isOnboardingVisible: true, stepIndex: 1 }),

      setAnswers: (patch) => set((state) => ({ answers: { ...state.answers, ...patch } })),

      toggleReason: (key) =>
        set((state) => {
          const held = state.answers.reasons;
          return {
            answers: {
              ...state.answers,
              reasons: held.includes(key) ? held.filter((r) => r !== key) : [...held, key],
            },
          };
        }),

      setConsent: (kind, granted) =>
        set((state) => ({
          answers: { ...state.answers, consents: { ...state.answers.consents, [kind]: granted } },
        })),

      next: () => {
        /* ⚠️⚠️ READ THROUGH `get()`, NOT A CAPTURED VALUE. A step's controls and
           its Continue button are rendered in the same pass, so a handler that
           closed over `answers` would judge the answer sheet as it was when the
           screen mounted — the classic stale-read that lets you leave a step you
           have not satisfied, or traps you on one you have. */
        const { stepIndex, answers } = get();
        const step = ONBOARDING_STEPS[stepIndex - 1];
        // ⚠️ Today is passed IN (the flow module reads no clock) — the birthday
        // gate compares against it.
        if (!step || !canAdvance(step.key, answers, formatDateToLocal(new Date()))) return;

        if (stepIndex >= TOTAL_STEPS) {
          void get().complete();
          return;
        }
        const nextIndex = stepIndex + 1;
        set({ stepIndex: nextIndex });
        // Resume point. Fire-and-forget: losing it costs a few taps, never data.
        // ⚠️ For THIS account — the save asks who is signed in only when its turn comes.
        void saveOnboarding({ current_step: nextIndex }, accountGeneration());
      },

      back: () => set((state) => ({ stepIndex: Math.max(1, state.stepIndex - 1) })),

      /* ⚠️⚠️ RE-ENTRANT UNTIL 2026-08-31, AND PRODUCTION HAS THE SCARS. `next()`
         fires this and returns immediately, while `isOnboardingVisible` is not
         cleared until the very end — two awaits later. The button therefore
         stayed live for the whole write, and a second click ran the whole thing
         again. `user_consents` is APPEND-ONLY, so it did not overwrite, it
         DUPLICATED: one real user has two complete sets of consent rows written
         1.5 seconds apart. A ledger whose entire purpose is answering "what did
         they agree to, and when" should not be able to answer twice.
         ⭐ The guard is the store's, not the button's. A disabled button is a
         courtesy; only this stops a second call however it arrives. */
      complete: async () => {
        if (get().isCompleting) return;
        set({ isCompleting: true });
        const { answers } = get();
        const at = accountGeneration();

        /* Each answer goes to the field that already owns it. ⚠️ None of these
           is duplicated into `user_onboarding` — see migration 0255's note on
           two doors onto one record. */
        const preferred = answers.preferredName.trim();
        if (preferred) useUserStore.getState().setName(preferred);
        if (answers.fullName.trim()) useUserStore.getState().setFullName(answers.fullName.trim());
        if (answers.dateOfBirth) useUserStore.getState().setBirthDate(answers.dateOfBirth);

        /* ⭐⭐ THE CITY (owner, 2026-09-18) — into the app's own location store,
           the same record Settings → Location writes, so the day clock, the
           prayer layer and the console's country/city view all read one fact.
           ⚠️ `setLocation` only when there IS one: an overwrite with a blank
           would wipe a location a returning user already had. */
        if (answers.city) {
          const { label, lat, lng, timeZone } = answers.city;
          useLocationStore.getState().setLocation({ label, lat, lng, timeZone }, 'preset');
        }

        /* ⚠️⚠️ ONLY WHEN ANSWERED. Skipping the identity step must leave
           `body_figure_gender` NULL — null means *unanswered*, and collapsing it
           to a default is the bug that rendered everyone who never opened
           Settings as a male figure in the Body Atlas. */
        if (answers.gender) useBodyAtlasStore.getState().setGender(answers.gender);

        /* ⭐ The consent's EFFECT, not just its record. Refusing the AI consent
           switches every source off; granting clears the refusals rather than
           pinning six `true`s, so a source added later still defaults to
           visible. */
        useUserStore.getState().setAllAiVisibility(aiSourcesAllowed(answers.consents));

        // The ledger — one row per answer, appended, never updated.
        /* ⚠️⚠️ THE GUARD MUST BE RELEASED ON FAILURE, OR IT IS WORSE THAN THE BUG
           IT FIXES. These two writes are the only things here that can throw,
           and a first draft of this guard left `isCompleting` true for ever on a
           dropped connection — locking the person out of finishing at all. The
           overlay deliberately stays OPEN on failure so the Enter button is
           still there to press again; `user_consents` being append-only means a
           retry after a genuine failure is safe, where a retry after a SUCCESS
           is what produced the duplicate rows in the first place. */
        try {
          /* ⚠️⚠️ Each write here asks who is signed in when its turn comes — after a
             sign-out here it would mark the NEXT account onboarded, with these answers.
             `at` makes it write nothing once the account has moved (lib/onboarding). */
          await recordConsents(CONSENTS.map((c) => ({ kind: c.kind, granted: answers.consents[c.kind] === true })), at);
          await saveOnboarding({
            reasons: answers.reasons,
            current_step: TOTAL_STEPS,
            completed_at: new Date().toISOString(),
          }, at);
        } catch (err) {
          // ⚠️ Was swallowed entirely: `next()` calls this with `void`, so a
          // rejection vanished and the overlay simply sat there.
          console.error('[onboarding] complete() failed; the sequence stays open so it can be retried', err);
          set({ isCompleting: false });
          return;
        }
        if (at !== accountGeneration()) return;

        get().setHasCompletedOnboarding(true);

        /* ⚠️⚠️ CHOOSE THE DESTINATION. Until 2026-09-01 this only hid itself and
           left you on whatever view happened to be current — which for the first
           real signup was `journal`, so someone finished onboarding and landed
           on an empty Reflect page. The last screen SAYS where you are going, so
           it has to actually go there: a promise the code does not keep is worse
           than no promise.
           ⭐ The ROADMAP since 2026-09-29 (owner): Home waits for step 4, and the
           Roadmap is where step 0 begins. The last screen says so. */
        useCurrentView.getState().setCurrentView(LANDING_VIEW);

        set({ isOnboardingVisible: false, isCompleting: false });
      },
    }),
    {
      name: 'onboarding-storage',
      /* ⚠️ One copy per account. The old shared copy goes to the account signed in when
         the page loads, never to one that signs in after (lib/accountStorage). */
      storage: accountStorage<Partial<OnboardingState>>('claim'),
      /* ⚠️ The answers are persisted so a refresh mid-sequence does not lose
         twelve screens of work even offline. `initializedUserId` is NOT
         persisted, so `initialize()` re-runs on every load and the cloud stays
         authoritative for whether onboarding is finished. */
      /* ⚠️ `isCompleting` is deliberately ABSENT. Persisting an in-flight flag
         means a refresh mid-write leaves it stuck true for ever, and the person
         can never finish onboarding again. */
      partialize: (state) => ({
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        stepIndex: state.stepIndex,
        answers: state.answers,
      }),
    },
  ),
);

scopeToAccount(useOnboardingStore);
